import React, { useState } from 'react';
import { PlusIcon, TrashIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { DASHBOARD_STATUSES } from '../types';

interface StatusMapEditorProps {
  statusMap: Record<string, string>;
  onChange: (statusMap: Record<string, string>) => void;
}

const inputCls =
  'w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-400';

const selectCls =
  'w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-400';

const StatusMapEditor: React.FC<StatusMapEditorProps> = ({ statusMap, onChange }) => {
  const [newSheetStatus, setNewSheetStatus] = useState('');
  const [newDbStatus, setNewDbStatus] = useState('');
  const [addError, setAddError] = useState<string | null>(null);

  const entries = Object.entries(statusMap);

  const handleChangeDbStatus = (sheetStatus: string, dbStatus: string) => {
    onChange({ ...statusMap, [sheetStatus]: dbStatus });
  };

  const handleRemove = (sheetStatus: string) => {
    const next = { ...statusMap };
    delete next[sheetStatus];
    onChange(next);
  };

  const handleAdd = () => {
    setAddError(null);
    if (!newSheetStatus.trim()) { setAddError('Sheet status value cannot be empty.'); return; }
    if (!newDbStatus) { setAddError('Please select a dashboard status.'); return; }
    onChange({ ...statusMap, [newSheetStatus.trim()]: newDbStatus });
    setNewSheetStatus('');
    setNewDbStatus('');
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Map each Status value from the sheet to a dashboard status. Rows whose Status has no
        mapping will fail during commit.
      </p>

      {/* Existing mappings */}
      {entries.length > 0 && (
        <div className="divide-y divide-gray-100 dark:divide-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          {entries.map(([sheetStatus, dbStatus]) => (
            <div key={sheetStatus} className="grid grid-cols-[1fr_auto_1fr_auto] items-center gap-3 px-4 py-2.5 bg-white dark:bg-gray-900">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate" title={sheetStatus}>
                {sheetStatus}
              </span>
              <span className="text-gray-400 dark:text-gray-600 text-sm">→</span>
              <select
                value={dbStatus}
                onChange={(e) => handleChangeDbStatus(sheetStatus, e.target.value)}
                className={selectCls}
              >
                {DASHBOARD_STATUSES.map((ds) => (
                  <option key={ds} value={ds}>{ds}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => handleRemove(sheetStatus)}
                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors flex-shrink-0"
                title="Remove"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add new mapping */}
      <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-4 space-y-3">
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          Add mapping
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-xs text-gray-500 dark:text-gray-400">Sheet status value</label>
            <input
              type="text"
              value={newSheetStatus}
              onChange={(e) => setNewSheetStatus(e.target.value)}
              placeholder="e.g. Submitted"
              className={inputCls}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-gray-500 dark:text-gray-400">Dashboard status</label>
            <select
              value={newDbStatus}
              onChange={(e) => setNewDbStatus(e.target.value)}
              className={selectCls}
            >
              <option value="">— choose —</option>
              {DASHBOARD_STATUSES.map((ds) => (
                <option key={ds} value={ds}>{ds}</option>
              ))}
            </select>
          </div>
        </div>
        {addError && (
          <p className="text-xs text-red-500 flex items-center gap-1">
            <ExclamationCircleIcon className="w-3.5 h-3.5 flex-shrink-0" />
            {addError}
          </p>
        )}
        <button
          type="button"
          onClick={handleAdd}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm"
        >
          <PlusIcon className="w-4 h-4" />
          Add
        </button>
      </div>
    </div>
  );
};

export default StatusMapEditor;
